import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { 
  ClipboardCheck, Plus, Trash2, Edit2, Calendar, User, Flag, 
  AlertCircle, CheckCircle2, Clock, X, Save, RefreshCw
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { toast } from 'sonner';
import { formatDate } from '@/utils/formatters';
import { useAuth } from '@/context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const PRIORITIES = ['Low', 'Medium', 'High', 'Critical'];
const IMPACTS = ['Low', 'Medium', 'High'];
const STATUSES = ['Open', 'In Progress', 'Completed', 'Cancelled'];

const PRIORITY_COLORS = {
  Low: 'bg-slate-600 text-slate-200',
  Medium: 'bg-blue-600 text-white',
  High: 'bg-orange-500 text-white',
  Critical: 'bg-red-600 text-white'
};

const IMPACT_COLORS = {
  Low: 'text-slate-400',
  Medium: 'text-yellow-400',
  High: 'text-red-400'
};

const STATUS_COLORS = {
  Open: 'bg-blue-950 text-blue-400 border-blue-500/30',
  'In Progress': 'bg-amber-950 text-amber-400 border-amber-500/30',
  Completed: 'bg-green-950 text-green-400 border-green-500/30',
  Cancelled: 'bg-slate-800 text-slate-400 border-slate-600'
};

const ActionItemsPanel = ({ isOpen, onClose }) => {
  const { user } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('active'); // active, all, completed
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [formData, setFormData] = useState({
    title: '',
    description: '',
    priority: 'Medium',
    impact: 'Medium',
    responsible_person: '',
    due_date: ''
  });

  useEffect(() => {
    if (isOpen) {
      fetchItems();
    }
  }, [isOpen]);

  const fetchItems = async () => {
    try {
      const response = await axios.get(`${BACKEND_URL}/api/action-items`);
      setItems(response.data);
    } catch (error) {
      console.error('Error fetching action items:', error);
      toast.error('Failed to load action items');
    } finally {
      setLoading(false);
    }
  };

  const handleAddNew = () => {
    setEditingItem(null);
    setFormData({
      title: '',
      description: '',
      priority: 'Medium',
      impact: 'Medium',
      responsible_person: '',
      due_date: ''
    });
    setShowAddDialog(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData({
      title: item.title,
      description: item.description || '',
      priority: item.priority,
      impact: item.impact,
      responsible_person: item.responsible_person || '',
      due_date: item.due_date ? item.due_date.split('T')[0] : ''
    });
    setShowAddDialog(true);
  };

  const handleSave = async () => {
    if (!formData.title.trim()) {
      toast.error('Title is required');
      return;
    }

    setSaving(true);
    try {
      const payload = {
        ...formData,
        due_date: formData.due_date ? new Date(formData.due_date).toISOString() : null,
        created_by: user?.name || 'User'
      };

      if (editingItem) {
        await axios.put(`${BACKEND_URL}/api/action-items/${editingItem.item_id}`, payload);
        toast.success('Action item updated');
      } else {
        await axios.post(`${BACKEND_URL}/api/action-items`, payload);
        toast.success('Action item created');
      }
      setShowAddDialog(false);
      fetchItems();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (item, newStatus) => {
    try {
      await axios.put(`${BACKEND_URL}/api/action-items/${item.item_id}`, { status: newStatus });
      toast.success(`Status updated to ${newStatus}`);
      fetchItems();
    } catch (error) {
      toast.error('Failed to update status');
    }
  };

  const handleDelete = async (item) => {
    if (!confirm(`Delete "${item.title}"?`)) return;

    try {
      await axios.delete(`${BACKEND_URL}/api/action-items/${item.item_id}`);
      toast.success('Action item deleted');
      fetchItems();
    } catch (error) {
      toast.error('Failed to delete');
    }
  };

  const filteredItems = items.filter(item => {
    if (filter === 'active') return item.status !== 'Completed' && item.status !== 'Cancelled';
    if (filter === 'completed') return item.status === 'Completed';
    return true;
  });

  const isOverdue = (item) => {
    if (!item.due_date || item.status === 'Completed' || item.status === 'Cancelled') return false;
    return new Date(item.due_date) < new Date();
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[100] flex items-center justify-end">
      {/* Backdrop */}
      <div className="absolute inset-0 bg-black/50 backdrop-blur-sm" onClick={onClose} />
      
      {/* Panel */}
      <div className="relative w-full max-w-lg h-full bg-slate-950 border-l border-cyan-500/20 shadow-2xl flex flex-col" data-testid="action-items-panel">
        {/* Header */}
        <div className="flex items-center justify-between p-4 border-b border-slate-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-500/20 border border-amber-500/30 flex items-center justify-center">
              <ClipboardCheck className="w-5 h-5 text-amber-400" />
            </div>
            <div>
              <h2 className="text-lg font-bold text-white uppercase tracking-wide">Action Items</h2>
              <p className="text-xs text-slate-500">{filteredItems.length} items</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            <Button onClick={handleAddNew} size="sm" className="bg-amber-500/20 hover:bg-amber-500/30 text-amber-400 border border-amber-500/50">
              <Plus className="w-4 h-4 mr-1" /> Add
            </Button>
            <Button onClick={onClose} size="sm" variant="ghost" className="text-slate-400 hover:text-white">
              <X className="w-5 h-5" />
            </Button>
          </div>
        </div>

        {/* Filter Tabs */}
        <div className="flex gap-1 p-2 bg-slate-900/50 border-b border-slate-800">
          {['active', 'completed', 'all'].map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-3 py-1.5 text-xs font-medium uppercase tracking-wider rounded transition-colors ${
                filter === f ? 'bg-amber-500/20 text-amber-400' : 'text-slate-400 hover:text-white'
              }`}
            >
              {f}
            </button>
          ))}
        </div>

        {/* Items List */}
        <div className="flex-1 overflow-y-auto p-3 space-y-2">
          {loading ? (
            <div className="flex items-center justify-center py-8">
              <RefreshCw className="w-6 h-6 animate-spin text-amber-400" />
            </div>
          ) : filteredItems.length === 0 ? (
            <div className="text-center py-8">
              <ClipboardCheck className="w-12 h-12 text-slate-600 mx-auto mb-3" />
              <p className="text-slate-500">No action items</p>
              <Button onClick={handleAddNew} size="sm" className="mt-3 bg-amber-500/20 text-amber-400">
                <Plus className="w-4 h-4 mr-1" /> Add First Item
              </Button>
            </div>
          ) : (
            filteredItems.map((item) => (
              <div
                key={item.item_id}
                className={`bg-slate-900/80 border rounded-lg p-3 ${
                  isOverdue(item) ? 'border-red-500/50' : 'border-slate-700'
                }`}
              >
                {/* Header Row */}
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="flex-1">
                    <h4 className={`font-medium ${item.status === 'Completed' ? 'text-slate-400 line-through' : 'text-white'}`}>
                      {item.title}
                    </h4>
                    {item.description && (
                      <p className="text-xs text-slate-500 mt-1 line-clamp-2">{item.description}</p>
                    )}
                  </div>
                  <div className="flex items-center gap-1">
                    <Button size="sm" variant="ghost" onClick={() => handleEdit(item)} className="h-7 w-7 p-0 text-slate-400 hover:text-cyan-400">
                      <Edit2 className="w-3.5 h-3.5" />
                    </Button>
                    <Button size="sm" variant="ghost" onClick={() => handleDelete(item)} className="h-7 w-7 p-0 text-slate-400 hover:text-red-400">
                      <Trash2 className="w-3.5 h-3.5" />
                    </Button>
                  </div>
                </div>

                {/* Tags Row */}
                <div className="flex flex-wrap items-center gap-2 mb-2">
                  <span className={`text-[10px] px-2 py-0.5 rounded font-bold uppercase ${PRIORITY_COLORS[item.priority]}`}>
                    {item.priority}
                  </span>
                  <span className={`text-[10px] flex items-center gap-1 ${IMPACT_COLORS[item.impact]}`}>
                    <AlertCircle className="w-3 h-3" />
                    {item.impact} Impact
                  </span>
                  {isOverdue(item) && (
                    <span className="text-[10px] px-2 py-0.5 rounded bg-red-600 text-white font-bold uppercase">
                      Overdue
                    </span>
                  )}
                </div>

                {/* Info Row */}
                <div className="flex flex-wrap items-center gap-3 text-xs text-slate-400 mb-2">
                  {item.responsible_person && (
                    <span className="flex items-center gap-1">
                      <User className="w-3 h-3" />
                      {item.responsible_person}
                    </span>
                  )}
                  {item.due_date && (
                    <span className={`flex items-center gap-1 ${isOverdue(item) ? 'text-red-400' : ''}`}>
                      <Calendar className="w-3 h-3" />
                      {formatDate(item.due_date)}
                    </span>
                  )}
                </div>

                {/* Status Selector */}
                <div className="flex items-center gap-2">
                  <span className="text-xs text-slate-500">Status:</span>
                  <Select value={item.status} onValueChange={(v) => handleStatusChange(item, v)}>
                    <SelectTrigger className={`h-7 text-xs w-32 border ${STATUS_COLORS[item.status]}`}>
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {STATUSES.map((s) => (
                        <SelectItem key={s} value={s}>{s}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            ))
          )}
        </div>

        {/* Footer Stats */}
        <div className="p-3 border-t border-slate-800 bg-slate-900/50">
          <div className="flex justify-between text-xs">
            <span className="text-slate-500">
              Open: <span className="text-blue-400 font-bold">{items.filter(i => i.status === 'Open').length}</span>
            </span>
            <span className="text-slate-500">
              In Progress: <span className="text-amber-400 font-bold">{items.filter(i => i.status === 'In Progress').length}</span>
            </span>
            <span className="text-slate-500">
              Completed: <span className="text-green-400 font-bold">{items.filter(i => i.status === 'Completed').length}</span>
            </span>
          </div>
        </div>
      </div>

      {/* Add/Edit Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="bg-slate-900 border-amber-500/30 max-w-md">
          <DialogHeader>
            <DialogTitle className="text-white uppercase tracking-wide flex items-center gap-2">
              <ClipboardCheck className="w-5 h-5 text-amber-400" />
              {editingItem ? 'Edit Action Item' : 'New Action Item'}
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label className="text-slate-400 uppercase text-xs">Title *</Label>
              <Input
                value={formData.title}
                onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                className="bg-slate-800 border-slate-700"
                placeholder="Action item title"
                data-testid="action-item-title"
              />
            </div>
            <div>
              <Label className="text-slate-400 uppercase text-xs">Description</Label>
              <Textarea
                value={formData.description}
                onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                className="bg-slate-800 border-slate-700"
                placeholder="Details..."
                rows={2}
              />
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-400 uppercase text-xs">Priority</Label>
                <Select value={formData.priority} onValueChange={(v) => setFormData({ ...formData, priority: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {PRIORITIES.map((p) => (
                      <SelectItem key={p} value={p}>{p}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs">Impact</Label>
                <Select value={formData.impact} onValueChange={(v) => setFormData({ ...formData, impact: v })}>
                  <SelectTrigger className="bg-slate-800 border-slate-700">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {IMPACTS.map((i) => (
                      <SelectItem key={i} value={i}>{i}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="grid grid-cols-2 gap-3">
              <div>
                <Label className="text-slate-400 uppercase text-xs">Responsible Person</Label>
                <Input
                  value={formData.responsible_person}
                  onChange={(e) => setFormData({ ...formData, responsible_person: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                  placeholder="Name"
                />
              </div>
              <div>
                <Label className="text-slate-400 uppercase text-xs">Due Date</Label>
                <Input
                  type="date"
                  value={formData.due_date}
                  onChange={(e) => setFormData({ ...formData, due_date: e.target.value })}
                  className="bg-slate-800 border-slate-700"
                />
              </div>
            </div>
            <div className="flex justify-end gap-3 pt-2">
              <Button variant="outline" onClick={() => setShowAddDialog(false)} className="border-slate-600">
                Cancel
              </Button>
              <Button onClick={handleSave} disabled={saving} className="bg-amber-600 hover:bg-amber-500">
                {saving ? <RefreshCw className="w-4 h-4 mr-2 animate-spin" /> : <Save className="w-4 h-4 mr-2" />}
                {editingItem ? 'Update' : 'Create'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default ActionItemsPanel;
